import { useEffect, useSyncExternalStore } from 'react';
import api from './api';
import { useAuthStore } from '../stores/authStore';

/**
 * Theme = mode (light / dark / system) + one skin for light and one for dark.
 * Stored in localStorage (gbr_theme, gbr_skin_light, gbr_skin_dark) and, when signed in, in the profile
 * (users.themePreference, themeLight, themeDark). The inline script in index.html applies it before first paint;
 * this module keeps <html data-theme / data-skin / data-form> in sync afterwards.
 * All values of a skin (colours, fonts, radii, shadows) live as tokens in styles/gbr-theme.css, rules in styles/gbr-skins.css.
 */
export type ThemePref = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';
export type SkinId = 'standard' | 'editorial' | 'warm' | 'bold' | 'glass' | 'tealslate' | 'anthracite';

export interface SkinInfo {
  id: SkinId;
  label: string;
  blurb: string;
  /** form skins change fonts/shape (html[data-form]); the others are colour-only */
  form: boolean;
  /** preview tile colours only: bg, card, text, muted, accent, line, radius */
  pv: [string, string, string, string, string, string, string];
}

export const LIGHT_SKINS: SkinInfo[] = [
  { id: 'standard', label: 'Standard', blurb: 'The classic GetBookReviews look.', form: false, pv: ['#f6f9fb', '#ffffff', '#0f2530', '#587080', '#0b7a76', '#dde7ec', '6px'] },
  { id: 'editorial', label: 'Editorial', blurb: 'Publisher look: cream, bordeaux, serif headings.', form: true, pv: ['#faf7f1', '#fffdf8', '#1d1a16', '#6b6458', '#7a1f2e', '#e2d9c7', '2px'] },
  { id: 'warm', label: 'Warm Minimal', blurb: 'Soft cream and terracotta, round shapes.', form: true, pv: ['#fbf6f0', '#ffffff', '#2a2420', '#7a6d62', '#b4452a', '#ecdfd0', '9px'] },
  { id: 'bold', label: 'Bold SaaS', blurb: 'Crisp indigo with gradients and lime.', form: true, pv: ['#f5f6ff', '#ffffff', '#0e1033', '#5b5f86', '#4f46e5', '#e1e4f8', '7px'] },
];
export const DARK_SKINS: SkinInfo[] = [
  { id: 'glass', label: 'Dark Glass', blurb: 'Frosted glass cards, violet and turquoise.', form: true, pv: ['#0a0a14', '#1a1a30', '#eef0ff', '#a3a6c4', '#6a59f2', '#2b2b46', '8px'] },
  { id: 'tealslate', label: 'Teal Slate', blurb: 'Soft blue-green, close to the light look.', form: false, pv: ['#17262c', '#203239', '#e9f1f3', '#a8bcc5', '#1a7f7a', '#355059', '6px'] },
  { id: 'anthracite', label: 'Warm Anthracite', blurb: 'Calm neutral grey with a teal accent.', form: false, pv: ['#1d2124', '#272c30', '#eceff0', '#adb8bd', '#1a7f7a', '#3b4348', '6px'] },
];
export const DEFAULT_SKIN: Record<Theme, SkinId> = { light: 'standard', dark: 'glass' };
const SKINS: Record<Theme, SkinInfo[]> = { light: LIGHT_SKINS, dark: DARK_SKINS };

const KEY = 'gbr_theme';
const SKEY: Record<Theme, string> = { light: 'gbr_skin_light', dark: 'gbr_skin_dark' };
const META: Record<string, string> = { light: '#f6f9fb', dark: '#0c171c', editorial: '#faf7f1', warm: '#fbf6f0', bold: '#f5f6ff', glass: '#0a0a14', tealslate: '#17262c', anthracite: '#1d2124' };
const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
const listeners = new Set<() => void>();

const valid = (v: unknown): v is ThemePref => v === 'light' || v === 'dark' || v === 'system';
export const validSkin = (mode: Theme, v: unknown): v is SkinId => SKINS[mode].some((s) => s.id === v);
function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(KEY);
    return valid(v) ? v : 'system';
  } catch {
    return 'system';
  }
}
function readSkin(mode: Theme): SkinId {
  try {
    const v = localStorage.getItem(SKEY[mode]);
    return validSkin(mode, v) ? v : DEFAULT_SKIN[mode];
  } catch {
    return DEFAULT_SKIN[mode];
  }
}

let pref: ThemePref = readPref();
const skins: Record<Theme, SkinId> = { light: readSkin('light'), dark: readSkin('dark') };
const resolve = (p: ThemePref): Theme => (p === 'system' ? (mq?.matches ? 'dark' : 'light') : p);
let snap = '';

function apply() {
  const t = resolve(pref);
  const skin = skins[t];
  const el = document.documentElement;
  if (t === 'dark') el.setAttribute('data-theme', 'dark');
  else el.removeAttribute('data-theme');
  if (skin === 'standard') el.removeAttribute('data-skin');
  else el.setAttribute('data-skin', skin);
  if (SKINS[t].find((s) => s.id === skin)?.form) el.setAttribute('data-form', '1');
  else el.removeAttribute('data-form');
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', META[skin] || META[t]);
  snap = `${pref}|${t}|${skins.light}|${skins.dark}`;
  listeners.forEach((l) => l());
}

if (typeof window !== 'undefined') {
  apply();
  mq?.addEventListener?.('change', () => pref === 'system' && apply());
  // other tabs
  window.addEventListener('storage', (e) => {
    if (e.key && (e.key === KEY || e.key === SKEY.light || e.key === SKEY.dark)) {
      pref = readPref();
      skins.light = readSkin('light');
      skins.dark = readSkin('dark');
      apply();
    }
  });
}

function persist() {
  try {
    localStorage.setItem(KEY, pref);
    localStorage.setItem(SKEY.light, skins.light);
    localStorage.setItem(SKEY.dark, skins.dark);
  } catch {
    /* private mode: still applies for this page view */
  }
}
function save() {
  if (useAuthStore.getState().isAuthenticated) {
    api.patch('/auth/theme', { themePreference: pref, themeLight: skins.light, themeDark: skins.dark }).catch(() => undefined);
  }
}

export function setThemePref(p: ThemePref, doSave = true) {
  pref = p;
  persist();
  apply();
  if (doSave) save();
}

export function setSkin(mode: Theme, id: SkinId, doSave = true) {
  if (!validSkin(mode, id)) return;
  skins[mode] = id;
  persist();
  apply();
  if (doSave) save();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function useTheme() {
  const s = useSyncExternalStore(subscribe, () => snap, () => snap);
  const [p, t, sl, sd] = s.split('|') as [ThemePref, Theme, SkinId, SkinId];
  return { pref: p, theme: t, skinLight: sl, skinDark: sd, setPref: setThemePref, setSkin };
}

/** Current value of a design token, e.g. cssVar('--t') - for libraries that need real colours (charts). */
export function cssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/** Mounted once in App: after sign-in the profile's choice wins; a profile without a choice gets the local one. */
export function ThemeSync() {
  const userId = useAuthStore((s) => (s.isAuthenticated ? s.user?._id || s.user?.id : undefined));
  useEffect(() => {
    if (!userId) return;
    let off = false;
    api
      .get('/auth/me')
      .then((r) => {
        if (off) return;
        const d = r.data?.data || {};
        const sp = d.themePreference;
        const sl = d.themeLight;
        const sd = d.themeDark;
        let push = false;
        if (valid(sp)) {
          if (sp !== pref) pref = sp;
        } else if (pref !== 'system') push = true;
        if (validSkin('light', sl)) skins.light = sl;
        else if (skins.light !== DEFAULT_SKIN.light) push = true;
        if (validSkin('dark', sd)) skins.dark = sd;
        else if (skins.dark !== DEFAULT_SKIN.dark) push = true;
        persist();
        apply();
        if (push) save();
      })
      .catch(() => undefined);
    return () => {
      off = true;
    };
  }, [userId]);
  return null;
}
